import type { SupabaseClient } from '@supabase/supabase-js'
import { createBrowserClient } from '@supabase/ssr'
import { navigatorLock } from '@supabase/auth-js'
import { NETWORK_CONSTANTS } from '@/lib/constants/network'
import { fetchWithNetworkTimeout } from '@/lib/fetchWithTimeout'
import { getSupabasePublicEnv } from '@/lib/env'
import { logger } from '@/lib/logger'
import {
  getBrowserSchema,
  pinIdentityTablesToPublicSchema,
  type DataSchema,
} from '@/lib/testMode'

let _instance: SupabaseClient | null = null
let _instanceSchema: DataSchema | null = null

/** @deprecated Import `NETWORK_CONSTANTS.SUPABASE_AUTH_LOCK_TIMEOUT_MS` instead. */
export const SUPABASE_AUTH_LOCK_TIMEOUT_MS = NETWORK_CONSTANTS.SUPABASE_AUTH_LOCK_TIMEOUT_MS

/** Clears the browser singleton so the next call picks up a new test-mode schema. */
export function resetSupabaseClient(): void {
  _instance = null
  _instanceSchema = null
}

export function getSupabaseClient(): SupabaseClient {
  const desiredSchema = getBrowserSchema()
  if (_instance && _instanceSchema !== desiredSchema) {
    resetSupabaseClient()
  }

  if (!_instance) {
    const { url, publishableKey } = getSupabasePublicEnv()
    if (typeof window !== 'undefined') {
      if (url.includes('db.') && url.includes('supabase')) {
        logger.error(
          '[Supabase] NEXT_PUBLIC_SUPABASE_URL appears to use the connection pooler (db. prefix). Use the Project URL from Dashboard → Project Settings → API instead, e.g. https://[ref].supabase.co'
        )
      }
      if (url.endsWith('supabase.com')) {
        logger.error('[Supabase] URL should end with .supabase.co not .supabase.com')
      }
    }
    // `getBrowserSchema()` returns `'public' | 'test'`, which makes
    // `createBrowserClient` infer a `SupabaseClient<any, 'public' | 'test', ...>`.
    // The `Database` generic isn't used anywhere in this module (everything
    // defaults to `any`), so the schema literal carries no real type-safety
    // value here. Cast back to the plain `SupabaseClient` shape that this
    // module's public API (and all of its callers) expect.
    _instance = createBrowserClient(url, publishableKey, {
      db: {
        schema: desiredSchema,
      },
      global: {
        fetch: fetchWithNetworkTimeout,
      },
      auth: {
        // Supabase Auth uses the browser LockManager for cross-tab token safety.
        lock: async (name, _acquireTimeout, fn) => {
          if (typeof navigator !== 'undefined' && navigator.locks) {
            return navigatorLock(
              name,
              NETWORK_CONSTANTS.SUPABASE_AUTH_LOCK_TIMEOUT_MS,
              fn
            )
          }
          return fn()
        },
      },
    }) as SupabaseClient
    pinIdentityTablesToPublicSchema(_instance)
    _instanceSchema = desiredSchema
  }
  return _instance
}
